import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { trackMetaEvent } from '@/utils/metaPixel';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft } from 'lucide-react';

export default function Auth() {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password || (!isLogin && !name)) {
      toast.error('Please fill in all fields');
      return;
    }
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      if (isLogin) {
        const user = await login(email, password);
        toast.success(`Welcome back, ${user.name}!`);
        const targetPath = user.onboarding_complete ? '/dashboard' : '/onboarding';
        await new Promise(resolve => setTimeout(resolve, 100));
        const storedToken = localStorage.getItem('token');
        const storedUser = localStorage.getItem('user');
        if (!storedToken || !storedUser) {
          console.warn('localStorage not persisted, retrying...');
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password })
          });
          const data = await response.json();
          localStorage.setItem('token', data.token);
          localStorage.setItem('user', JSON.stringify(data.user));
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        window.location.replace(targetPath);
      } else {
        await register(email, password, name);
        toast.success('Account created successfully!');
        trackMetaEvent('CompleteRegistration', { content_name: 'Email Signup' });
        await new Promise(resolve => setTimeout(resolve, 100));
        window.location.replace('/onboarding');
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Something went wrong';
      toast.error(message);
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex" style={{ background: '#FAF8F5' }}>

      {/* LEFT — Form */}
      <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-20 py-12" style={{ background: '#FFFFFF' }}>
        <div className="max-w-[400px] mx-auto w-full">
          {/* Back + Logo */}
          <Link to="/" className="inline-flex items-center gap-2 mb-10 group" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4 text-[#8B7355] group-hover:text-[#2D5016] transition-colors" />
            <span className="text-sm" style={{ fontFamily: "'DM Sans', sans-serif", color: '#8B7355' }}>Back to home</span>
          </Link>

          <img src="/logo.png" alt="Let's Talk Money" className="h-12 w-auto mb-10" />

          {/* Heading */}
          <h1 className="mb-2" style={{ fontFamily: "'Playfair Display', serif", fontSize: '2rem', fontWeight: 700, color: '#1A2E1A' }} data-testid="auth-heading">
            {isLogin ? 'Welcome back' : 'Create your account'}
          </h1>
          <p className="mb-8" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', color: '#8B7355' }}>
            {isLogin ? 'Please enter your details' : 'Start your journey to better financial communication'}
          </p>

          {/* Google */}
          <button
            type="button"
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 h-12 rounded-lg transition-colors"
            style={{ border: '1px solid #D4C9B8', background: '#fff', fontFamily: "'DM Sans', sans-serif", fontSize: '14px', fontWeight: 500, color: '#1A2E1A' }}
            onMouseEnter={e => e.currentTarget.style.background = '#FAF8F5'}
            onMouseLeave={e => e.currentTarget.style.background = '#fff'}
            data-testid="google-signin-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            Sign in with Google
          </button>

          {/* Divider */}
          <div className="relative my-7">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full" style={{ borderTop: '1px solid #E8E0D4' }} />
            </div>
            <div className="relative flex justify-center">
              <span className="px-4 text-sm" style={{ background: '#FFFFFF', color: '#8B7355', fontFamily: "'DM Sans', sans-serif" }}>or</span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="auth-form">
            {!isLogin && (
              <div className="space-y-1.5">
                <Label htmlFor="name" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 500, color: '#5A5040' }}>Full name</Label>
                <Input
                  id="name" type="text" value={name} onChange={e => setName(e.target.value)}
                  placeholder="John Doe"
                  className="h-12 rounded-lg"
                  style={{ border: '1px solid #D4C9B8', fontFamily: "'DM Sans', sans-serif", fontSize: '14px' }}
                  data-testid="name-input"
                />
              </div>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="email" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 500, color: '#5A5040' }}>Email address</Label>
              <Input
                id="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-12 rounded-lg"
                style={{ border: '1px solid #D4C9B8', fontFamily: "'DM Sans', sans-serif", fontSize: '14px' }}
                data-testid="email-input"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '13px', fontWeight: 500, color: '#5A5040' }}>Password</Label>
              <div className="relative">
                <Input
                  id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="h-12 rounded-lg pr-12"
                  style={{ border: '1px solid #D4C9B8', fontFamily: "'DM Sans', sans-serif", fontSize: '14px' }}
                  data-testid="password-input"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: '#8B7355' }}
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button
              type="submit" disabled={loading}
              className="w-full h-12 rounded-lg font-medium text-[15px]"
              style={{ background: '#1A2E1A', color: '#fff', fontFamily: "'DM Sans', sans-serif" }}
              data-testid="auth-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {isLogin ? 'Signing in...' : 'Creating account...'}
                </span>
              ) : (
                isLogin ? 'Sign in' : 'Create account'
              )}
            </Button>
          </form>

          {/* Toggle */}
          <p className="mt-8 text-center text-sm" style={{ fontFamily: "'DM Sans', sans-serif", color: '#8B7355' }}>
            {isLogin ? "Don't have an account? " : 'Already have an account? '}
            <button type="button" onClick={() => setIsLogin(!isLogin)}
              className="font-semibold hover:underline" style={{ color: '#2D5016' }}
              data-testid="toggle-auth-mode"
            >
              {isLogin ? 'Sign up' : 'Sign in'}
            </button>
          </p>
        </div>
      </div>

      {/* RIGHT — Photo */}
      <div className="hidden lg:block flex-1 relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1604881991405-b273c7a4386a?w=1200&q=80"
          alt="Couple having a warm conversation over coffee"
          className="absolute inset-0 w-full h-full object-cover"
        />
        {/* Bottom gradient + text */}
        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(10,25,10,0.75) 0%, rgba(10,25,10,0.2) 40%, transparent 65%)' }} />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <h2 className="mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(24px, 2.5vw, 36px)', fontWeight: 700, color: '#FFFFFF', lineHeight: 1.2 }}>
            Practice makes<br />prosperity.
          </h2>
          <p style={{ fontFamily: "'DM Sans', sans-serif", fontSize: '15px', color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, maxWidth: 380 }}>
            Build confidence in money conversations with AI-powered coaching. No credit card required.
          </p>
        </div>
      </div>
    </div>
  );
}
